import * as React from 'react';
import { IGroupedComboBoxGroup, SectionScrollPosition } from 'grouped-combobox-core';
import { ISection } from './Section.types';

export function useSectionKeyDownHandler(
    sectionRefs: React.RefObject<ISection>[],
    groups: IGroupedComboBoxGroup[],
    onDismiss?: (ev?: unknown) => void
): (ev: React.KeyboardEvent<HTMLElement>) => void {
    return React.useCallback(
        (ev: React.KeyboardEvent<HTMLElement>) => {
            if (groups.length === 0) {
                return;
            }

            let targetGroupIndex: number | undefined = undefined;
            let targetItemPosition: SectionScrollPosition | undefined;

            switch (ev.key) {
                case 'ArrowDown':
                    if (ev.ctrlKey || ev.metaKey) {
                        const currentGroupIndex = sectionRefs.findIndex(
                            (sectionRef) => sectionRef.current?.hasFocus
                        );

                        if (currentGroupIndex > -1 && currentGroupIndex + 1 < groups.length) {
                            // Ctrl + Down: focus on the first item of the next group
                            targetGroupIndex = currentGroupIndex + 1;
                            targetItemPosition = 'FirstElement';
                        }
                    }
                    break;

                case 'ArrowUp':
                    if (ev.ctrlKey || ev.metaKey) {
                        const currentGroupIndex = sectionRefs.findIndex(
                            (sectionRef) => sectionRef.current?.hasFocus
                        );
                        if (currentGroupIndex > 0) {
                            // Ctrl + Up: focus on the last item of previous group
                            targetGroupIndex = currentGroupIndex - 1;
                            targetItemPosition = 'LastElement';
                        }
                    } else if (ev.altKey) {
                        // close the dropdown if Alt + Up is pressed anywhere in the L1 menu
                        ev.stopPropagation();
                        onDismiss?.(ev);
                        return;
                    }
                    break;

                case 'Home':
                    // Home: focus on the first item of the first group
                    targetGroupIndex = 0;
                    targetItemPosition = 'FirstElement';
                    break;

                case 'End':
                    // End: focus on the last item of the last group
                    targetGroupIndex = groups.length - 1;
                    targetItemPosition = 'LastElement';
                    break;

                case 'Tab':
                    // This is the catch-all case for when Tab is pressed on any non-actionable element within
                    // the menu dropdown (e.g. group header collapse button, item icon, etc.). We want to close
                    // the dropdown in such cases.
                    // The reason we have to initiate closing the dropdown here (rather than at the root GroupedComboBox element)
                    // is that the parent FocusZone element eats the Tab key event.
                    onDismiss?.(ev);
                    return;
            }

            if (targetGroupIndex !== undefined && targetItemPosition) {
                const targetSectionRef = sectionRefs[targetGroupIndex];
                targetSectionRef.current?.scrollToElement(targetItemPosition);

                ev.stopPropagation();
                ev.preventDefault();
            }
        },
        [sectionRefs, groups, onDismiss]
    );
}
